import { useEffect, useRef, useState } from "react";
import { Heart, MessageCircle, Share2, Music2, ChevronsLeftRight } from "lucide-react";
import { stickerSrc } from "@/components/landing/data";
import { useI18n } from "@/context/I18nContext";

export const FORMATS = {
  "9:16": { pad: "177.78%", label: "9:16 · TikTok · Reels · Shorts", cam: { top: "0%", left: "0%", width: "100%", height: "40%", r: "0" }, game: { top: "40%", height: "60%" } },
  "1:1": { pad: "100%", label: "1:1 · Instagram · Facebook", cam: { top: "4%", left: "4%", width: "38%", height: "30%", r: "14px" }, game: { top: "0%", height: "100%" } },
  "16:9": { pad: "56.25%", label: "16:9 · YouTube · Twitter", cam: { top: "6%", left: "3%", width: "27%", height: "36%", r: "10px" }, game: { top: "0%", height: "100%" } },
};
const ORDER = ["9:16", "1:1", "16:9"];
const EASE = "cubic-bezier(0.65, 0, 0.25, 1)";
const T = `top 0.8s ${EASE}, left 0.8s ${EASE}, width 0.8s ${EASE}, height 0.8s ${EASE}, border-radius 0.8s ${EASE}, transform 1.2s ease`;

export const useFormatCycle = (on, ms = 3200) => {
  const [i, setI] = useState(0);
  const [pause, setPause] = useState(0);
  useEffect(() => {
    if (!on) return undefined;
    let h;
    const start = setTimeout(() => { h = setInterval(() => setI((x) => (x + 1) % ORDER.length), ms); }, pause ? 6000 : 0);
    return () => { clearTimeout(start); clearInterval(h); };
  }, [on, ms, pause]);
  const go = (n) => { setI(((n % ORDER.length) + ORDER.length) % ORDER.length); setPause((p) => p + 1); };
  return [ORDER[i], i, go];
};

const useSwipe = (onLeft, onRight) => {
  const x0 = useRef(null);
  return {
    onTouchStart: (e) => { x0.current = e.touches[0].clientX; },
    onTouchEnd: (e) => {
      if (x0.current === null) return;
      const dx = e.changedTouches[0].clientX - x0.current;
      if (dx < -40) onLeft();
      if (dx > 40) onRight();
      x0.current = null;
    },
  };
};

export const PhoneClip = ({ ex, title, testid, format, cycle = 0 }) => {
  const [auto, idx, go] = useFormatCycle(!format, cycle || 3200);
  const swipe = useSwipe(() => go(idx + 1), () => go(idx - 1));
  const f = format || auto;
  const F = FORMATS[f];
  const wide = f === "16:9";
  return (
    <div className="relative w-full touch-pan-y" data-testid={testid} data-format={f} {...(format ? {} : swipe)}>
      <div className={`relative w-full p-[5px] bg-gradient-to-b from-[#ff4fd8]/60 via-white/10 to-[#a746ff]/70 shadow-[0_30px_80px_-20px_rgba(167,70,255,0.55)] ${wide ? "rounded-2xl" : "rounded-[2rem]"}`}
        style={{ transition: `border-radius 0.8s ${EASE}` }}>
        <div className={`relative w-full overflow-hidden bg-black ${wide ? "rounded-[0.8rem]" : "rounded-[1.6rem]"}`} style={{ paddingTop: F.pad, transition: `padding-top 0.8s ${EASE}` }}>
          <img src={ex.game} alt="" className="cx-clip-game absolute left-0 w-full object-cover" style={{ top: F.game.top, height: F.game.height, transition: T }} />
          <div className="absolute overflow-hidden border-2 border-[#ff4fd8]/70 shadow-[0_0_18px_rgba(255,79,216,0.45)]"
            style={{ top: F.cam.top, left: F.cam.left, width: F.cam.width, height: F.cam.height, borderRadius: F.cam.r, transition: T }} data-testid={`${testid}-cam`}>
            <img src={ex.cam} alt="" className="h-full w-full object-cover object-[50%_30%]" />
            <span className="absolute top-1.5 left-1.5 flex items-center gap-1 rounded bg-red-600 px-1.5 py-0.5 font-mono text-[8px] font-bold text-white"><span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />LIVE</span>
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/75 pointer-events-none" />
          <div className={`absolute top-2.5 right-2.5 flex items-center gap-1.5 ${wide ? "flex-col items-end" : ""}`}>
            <span className="font-mono text-[9px] tracking-widest px-2 py-0.5 rounded bg-black/70 text-[#3ad7ff] border border-[#3ad7ff]/30">{ex.tag}</span>
            <span className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-[#a746ff]/85 text-white">{ex.score}</span>
          </div>
          <div className={`absolute left-1/2 -translate-x-1/2 ${wide ? "top-[18%] w-[42%]" : f === "1:1" ? "top-[38%] w-[62%]" : "top-[46%] w-[74%]"}`} style={{ transition: `all 0.8s ${EASE}` }}>
            <div className="cx-st cx-clip-sticker" style={{ "--d": "60s" }} data-anim={ex.anim}>
              <div key={f} className={`cx-st-${ex.anim}`}><img src={stickerSrc(ex.sticker)} alt="" className="w-full drop-shadow-[0_10px_14px_rgba(0,0,0,0.65)]" /></div>
            </div>
          </div>
          {!wide && (
            <div className="absolute right-2.5 bottom-20 flex flex-col items-center gap-3.5 text-white/90">
              {[Heart, MessageCircle, Share2].map((I, i) => <I key={i} className="w-5 h-5 drop-shadow" />)}
            </div>
          )}
          <div className={`absolute bottom-0 inset-x-0 ${wide ? "p-2.5" : "p-4"}`}>
            <p className={`font-display text-white font-bold leading-snug pr-10 ${wide ? "text-xs" : "text-sm"}`}>{title}</p>
            {!wide && <p className="text-[11px] text-[#3ad7ff] mt-1 truncate">{ex.hashtags}</p>}
            <p className="text-[10px] text-white/60 mt-1 flex items-center gap-1"><Music2 className="w-3 h-3" /> CLIP-EX · original sound</p>
          </div>
        </div>
      </div>
      <div className="mt-3 flex justify-center">
        <span key={f} className="cx-rise font-mono text-[10px] tracking-wider rounded-full border border-[#ff4fd8]/40 bg-[#ff4fd8]/10 px-3 py-1 text-[#ff9df0]" data-testid={`${testid}-format`}>{F.label}</span>
      </div>
      {!format && <SwipeDots idx={idx} go={go} testid={testid} />}
    </div>
  );
};

const SwipeDots = ({ idx, go, testid }) => {
  const { t } = useI18n();
  return (
    <div className="lg:hidden mt-2.5 flex flex-col items-center gap-1.5" data-testid={`${testid}-swipe`}>
      <div className="flex gap-2">
        {ORDER.map((o, n) => <button key={o} type="button" aria-label={o} onClick={() => go(n)} data-testid={`${testid}-dot-${n}`}
          className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${n === idx ? "w-6 bg-[#ff4fd8] shadow-[0_0_8px_#ff4fd8]" : "w-1.5 bg-white/25"}`} />)}
      </div>
      <span className="flex items-center gap-1 text-[10px] text-slate-500"><ChevronsLeftRight className="w-3 h-3" />{t("ld.swipe_hint")}</span>
    </div>
  );
};
